import { useEffect, useMemo, useRef, useState } from "react";
import { BsCashStack, BsCheckCircleFill } from "react-icons/bs";
import { FiArrowUpRight, FiFilePlus, FiFileText } from "react-icons/fi";
import { LuDownload, LuEye } from "react-icons/lu";
import { MdCancel } from "react-icons/md";
import { RiDeleteBinLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import ConfirmDeleteModal from "../../../components/modals/ConfirmDeleteModal";
import NewTableComponent from "../../../components/NewTableComponent";
import Stats from "../../../components/Stats";
import UserAvatar from "../../../components/UserAvatar";
import CustomerFilterDropdown from "../../../components/CustomerFilterDropdown";
import DateFilterDropdown from "../../../components/DateFilterDropdown";
import VehicleFilterDropdown from "../../../components/VehicleFilterDropdown";
import ExportButton from "../../../components/ExportButton";
import { plusIcon } from "../../../utils/svgs";
import { useInvoicesListingExport } from "./useInvoicesListingExport";
import { apiService } from "../../../services/apiService";
import "../../../styles/Access.css";
import "../../../styles/AssetsRegistration.css";
import "../../../styles/DriverSafety.css";
import "../../../styles/Invoices.css";
import "../../../styles/Services.css";
import "../../../styles/Table.css";
import "../../../styles/Trip.css";
import "../../../styles/TripCostEstimation.css";
import {
  AppMainModules,
  FinancePermission,
  FinanceSubModules,
} from "../../../utils/app-constant";
import { hasPermission } from "../../../utils/helper";
import { errorNotify, successNotify } from "../../../utils/toast";

const DEFAULT_INVOICE_SEQUENCE = 1000;

const cloneValue = (value) => JSON.parse(JSON.stringify(value));
const toNumber = (value, fallback = 0) => {
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : fallback;
};
const safeArray = (value) => (Array.isArray(value) ? value : []);
const resolveOptionalNumber = (value, fallback = 0) => {
  if (value === null || value === undefined) return fallback;
  const normalizedValue = String(value).trim();
  if (normalizedValue === "") return fallback;
  return toNumber(normalizedValue, fallback);
};

const resolveApiFileUrl = (value = "") => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return "";
  if (/^(https?:|blob:|data:)/i.test(normalizedValue)) return normalizedValue;
  return `${process.env.REACT_APP_API_FILES_URL || ""}${normalizedValue}`;
};

export const formatAmount = (value) =>
  new Intl.NumberFormat("en-PK", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

export const invoiceStatusOptions = [
  { label: "Pending", value: "pending" },
  { label: "New", value: "new" },
  { label: "Submitted", value: "submitted" },
  { label: "Paid", value: "paid" },
  { label: "Approved", value: "approved" },
  { label: "Rejected", value: "rejected" },
];

const DEFAULT_NOTES_HTML = [
  "<p>Ensure transporter provides valid insurance and updated vehicle documents before dispatch.</p>",
  "<p>Verify that vehicle registration, fitness certificate, and pollution control documents are current. Request a copy of goods-in-transit insurance for each load.</p>",
].join("");

const resolveInvoiceNotes = (value, fallback = DEFAULT_NOTES_HTML) => {
  if (value === undefined || value === null) {
    return fallback;
  }
  return value;
};

export const buildBulkInvoiceRecordFromForm = ({
  values = {},
  existingRecord = null,
  createdBy = "Finance",
  createdByImage = "",
  calculationOverride = null,
}) => {
  const calculation =
    calculationOverride ||
    values.invoiceInfoCalculation ||
    existingRecord?.calculation;
  const resolvedNotes = resolveInvoiceNotes(
    values.notes,
    resolveInvoiceNotes(existingRecord?.notes, DEFAULT_NOTES_HTML),
  );
  const nextId =
    existingRecord?.id ||
    `invoice-${String(values.invoiceId || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")}`;

  return {
    id: nextId,
    invoiceType: "bulk",
    invoiceId: values.invoiceId || existingRecord?.invoiceId || "",
    customerId: values.customerId || "",
    contractId: values.contractId || existingRecord?.contractId || "",
    contractName: values.contractLabel || existingRecord?.contractName || "",
    customerName: values.customerLabel || existingRecord?.customerName || "",
    billingPeriod: values.billingPeriod || "",
    selectedTripIds: safeArray(values.selectedTripIds),
    selectedTrips: safeArray(values.selectedTrips),
    tripId: "",
    tripLabel: "",
    tripsCount: Math.max(1, safeArray(values.selectedTripIds).length),
    invoiceDate: values.invoice_at || values.invoiceDate || "",
    dueDate: values.invoice_due_at || values.dueDate || "",
    status: String(
      values.status || existingRecord?.status || "new",
    ).toLowerCase(),
    createdBy: existingRecord?.createdBy || createdBy,
    createdByImage: existingRecord?.createdByImage || createdByImage,
    notes: resolvedNotes,
    formValues: {
      notes: resolvedNotes,
      ...values,
    },
    calculation,
    tableTotalAmount:
      calculation?.totalAmount || calculation?.total_amount || 0,
  };
};

const formatTableDate = (value = "") => {
  const [year, month, day] = String(value || "").split("-");
  if (!year || !month || !day) return "-";
  return `${year}-${day}-${month}`;
};

const parseInvoiceSequence = (invoiceId = "") => {
  const matchedDigits = String(invoiceId || "").match(/(\d+)/g);
  if (!matchedDigits?.length) return 0;
  return Number(matchedDigits[matchedDigits.length - 1]) || 0;
};

export const getInvoiceRecords = () => [];

export const saveInvoiceRecords = (records = []) => {};

export const findInvoiceRecord = (records = [], id = "") =>
  safeArray(records).find(
    (record) => String(record?.id ?? "") === String(id ?? ""),
  ) || null;

export const getNextInvoicePreviewId = (records = []) => {
  const maxSequence = Math.max(
    0,
    ...safeArray(records).map((record) =>
      parseInvoiceSequence(record?.invoiceId || record?.trips_invoice_id),
    ),
  );

  return `TINV-${String(maxSequence + 1)}`;
};

export const buildInvoiceOverview = (records = []) =>
  safeArray(records).reduce(
    (overview, record) => ({
      totalInvoices: overview.totalInvoices + 1,
      newInvoices:
        overview.newInvoices +
        (["new", "pending"].includes(String(record?.status || "").toLowerCase())
          ? 1
          : 0),
      finalizedInvoices:
        overview.finalizedInvoices +
        (String(record?.status || "").toLowerCase() === "paid" ? 1 : 0),
      totalRevenue:
        overview.totalRevenue +
        toNumber(record?.tableTotalAmount, record?.calculation?.totalAmount),
    }),
    {
      totalInvoices: 0,
      newInvoices: 0,
      finalizedInvoices: 0,
      totalRevenue: 0,
    },
  );

export const buildInvoiceTableRows = (records = []) =>
  safeArray(records).map((record) => ({
    id: record?.id,
    invoiceType: record?.invoiceType || "single",
    invoiceId: record?.invoiceId || "-",
    customerName: record?.customerName || "-",
    tripsCount: record?.tripsCount > 0 ? record.tripsCount : "-",
    invoiceDateValue: record?.invoiceDate || "",
    invoiceDateDisplay: formatTableDate(record?.invoiceDate),
    totalAmount: toNumber(
      record?.tableTotalAmount,
      record?.calculation?.totalAmount,
    ),
    status: String(record?.status || "new"),
    createdBy: record?.createdBy || "Finance",
    createdByImage: record?.createdByImage || "",
  }));

export const normalizeInvoiceSearchText = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

export const matchesInvoiceSearch = (row = {}, searchText = "") => {
  const normalizedSearch = normalizeInvoiceSearchText(searchText);
  if (!normalizedSearch) return true;

  const haystack = [
    row?.invoiceId,
    row?.customerName,
    row?.tripsCount,
    row?.invoiceDateDisplay,
    row?.status,
    row?.createdBy,
    row?.totalAmount,
  ]
    .join(" ")
    .toLowerCase();

  return haystack.includes(normalizedSearch);
};

export const getInvoiceDefaultNotes = () => DEFAULT_NOTES_HTML;

const buildStatIcon = (icon) => (
  <span className="finance_metric_icon">{icon}</span>
);

const normalizeText = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const toComparableDateValue = (value) => {
  if (!value) return Number.NaN;
  const [year, day, month] = String(value || "").split("-");
  const parsedDate = new Date(`${year}-${month}-${day}`);
  return Number.isNaN(parsedDate.getTime()) ? Number.NaN : parsedDate.getTime();
};

const matchesFilter = (rowValue, filterMeta = {}) => {
  const filterType = filterMeta?.filter_type;
  const filterValue = filterMeta?.value;
  const rowValues = Array.isArray(rowValue) ? rowValue : [rowValue];
  const normalizedRowValues = rowValues.map((value) => normalizeText(value));

  if (
    !filterType ||
    filterValue === undefined ||
    filterValue === null ||
    filterValue === ""
  ) {
    return true;
  }

  if (filterType === "one_of") {
    const filterValues = safeArray(filterValue).map((value) =>
      normalizeText(value),
    );
    return normalizedRowValues.some((value) => filterValues.includes(value));
  }

  if (filterType === "contains") {
    return normalizedRowValues.some((value) =>
      value.includes(normalizeText(filterValue)),
    );
  }

  if (filterType === "equals" || filterType === "equal_to") {
    const normalizedFilterValue = normalizeText(filterValue);
    return normalizedRowValues.some((value) => value === normalizedFilterValue);
  }

  const comparableRowValue = rowValues[0];
  const rowDateValue = toComparableDateValue(comparableRowValue);
  const filterDateValue = toComparableDateValue(filterValue);

  if (!Number.isNaN(rowDateValue) && !Number.isNaN(filterDateValue)) {
    if (filterType === "less_than") return rowDateValue < filterDateValue;
    if (filterType === "greater_than") return rowDateValue > filterDateValue;
    if (filterType === "less_than_equal_to")
      return rowDateValue <= filterDateValue;
    if (filterType === "greater_than_equal_to")
      return rowDateValue >= filterDateValue;
  }

  const rowNumberValue = Number(comparableRowValue);
  const filterNumberValue = Number(filterValue);

  if (Number.isFinite(rowNumberValue) && Number.isFinite(filterNumberValue)) {
    if (filterType === "less_than") return rowNumberValue < filterNumberValue;
    if (filterType === "greater_than")
      return rowNumberValue > filterNumberValue;
    if (filterType === "less_than_equal_to")
      return rowNumberValue <= filterNumberValue;
    if (filterType === "greater_than_equal_to")
      return rowNumberValue >= filterNumberValue;
  }

  return true;
};

const statusMeta = {
  pending: {
    className: "is_submitted",
    label: "Pending",
    icon: <FiArrowUpRight />,
  },
  new: {
    className: "is_new",
    label: "New",
    icon: <FiFileText />,
  },
  submitted: {
    className: "is_submitted",
    label: "Submitted",
    icon: <FiArrowUpRight />,
  },
  paid: {
    className: "is_paid",
    label: "Paid",
    icon: <BsCheckCircleFill />,
  },
  approved: {
    className: "is_paid",
    label: "Approved",
    icon: <BsCheckCircleFill />,
  },
  rejected: {
    className: "is_rejected",
    label: "Rejected",
    icon: <MdCancel />,
  },
};

const formatApiDateForInput = (value = "") => {
  const normalizedValue = String(value || "").trim();
  if (!normalizedValue) return "";
  return normalizedValue.split("T")[0] || "";
};

const normalizeApiInvoiceType = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase() === "multiple"
    ? "bulk"
    : "single";

const normalizeApiInvoiceStatus = (value = "") =>
  String(value || "")
    .trim()
    .toLowerCase() || "pending";

const buildApiInvoiceCalculation = (invoice = {}) => {
  return {
    lineItems: [],
    subtotal: toNumber(invoice?.subtotal),
    salesTax: toNumber(invoice?.tax),
    discount: toNumber(invoice?.discount),
    totalAmount: toNumber(invoice?.overall_net_amount ?? invoice?.net_amount),
    rawData: invoice,
  };
};

const buildApiInvoiceRecord = (invoice = {}) => {
  const invoiceType = normalizeApiInvoiceType(invoice?.invoice_type);
  const invoiceId = invoice?.trips_invoice_id || "-";
  const invoiceDate = formatApiDateForInput(
    invoice?.invoice_at || invoice?.created_at || "-",
  );
  const dueDate = formatApiDateForInput(invoice?.invoice_due_at);
  const customerId = String(
    invoice?.customer?.id ?? invoice?.customer_id ?? "",
  ).trim();
  const customerName = invoice?.customer?.name || "-";
  const contractId = String(
    invoice?.contract?.id ?? invoice?.contract_template_id ?? "",
  ).trim();
  const contractName = invoice?.contract?.contracts_template_id || "";
  const status = normalizeApiInvoiceStatus(invoice?.status);
  const notes = resolveInvoiceNotes(invoice?.notes, DEFAULT_NOTES_HTML);
  const tripsCount =
    invoiceType === "single"
      ? 1
      : toNumber(
          invoice?.trips_count ??
            invoice?.trip_count ??
            invoice?.settlements?.length,
          0,
        );
  const calculation = buildApiInvoiceCalculation(invoice);

  return {
    id: String(invoice?.id ?? ""),
    invoiceType,
    invoiceId,
    customerId,
    customerName,
    contractId,
    contractName,
    billingPeriod: invoice?.billing_period || "",
    selectedTripIds: [],
    selectedTrips: [],
    tripId: "",
    tripRecordId: "",
    tripLabel: "",
    tripsCount,
    invoiceDate,
    dueDate,
    status,
    createdBy: invoice?.created_by_user?.username || "Finance",
    createdByImage: invoice?.created_by_user?.image || "",
    notes,
    formValues: {
      invoiceId,
      customerId,
      customerLabel: customerName,
      contractId,
      contractLabel: contractName,
      invoiceDate,
      dueDate,
      status,
      notes,
      selectedTripIds: [],
      selectedTrips: [],
    },
    calculation,
    tableTotalAmount: toNumber(invoice?.overall_net_amount ?? invoice?.net_amount),
  };
};

const Invoices = () => {
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const canCreateInvoices = hasPermission({
    permissionSlug: FinancePermission.Create,
    menuSlug: FinanceSubModules.Invoices,
    moduleSlug: AppMainModules.finance,
  });
  const canUpdateInvoices = hasPermission({
    permissionSlug: FinancePermission.Update,
    menuSlug: FinanceSubModules.Invoices,
    moduleSlug: AppMainModules.finance,
  });
  const canDeleteInvoices = hasPermission({
    permissionSlug: FinancePermission.Delete,
    menuSlug: FinanceSubModules.Invoices,
    moduleSlug: AppMainModules.finance,
  });
  const canViewInvoices = hasPermission({
    permissionSlug: FinancePermission.View,
    menuSlug: FinanceSubModules.Invoices,
    moduleSlug: AppMainModules.finance,
  });

  const [records, setRecords] = useState([]);
  const [overview, setOverview] = useState({
    totalInvoices: 0,
    outstandingAmount: 0,
    paidAmount: 0,
    totalRevenue: 0,
  });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [searchText, setSearchText] = useState("");
  const [searchFilters, setSearchFilters] = useState({});
  const [paginationMeta, setPaginationMeta] = useState({
    totalItems: 0,
    totalPages: 1,
    currentPage: 1,
  });
  const [deletePopup, setDeletePopup] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showCreateDropdown, setShowCreateDropdown] = useState(false);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);

  const {
    hasActiveFilters,
    exportLoading,
    pdfExportLoading,
    excelExportLoading,
    handleExportPdf,
    handleExportExcel,
  } = useInvoicesListingExport(searchText, searchFilters);

  useEffect(() => {
    let isMounted = true;
    const fetchCustomers = async () => {
      try {
        const response = await apiService.getCustomerDropdown();
        const data =
          response?.data?.results || response?.results || response?.data || [];
        if (isMounted) {
          setCustomerOptions(
            Array.isArray(data)
              ? data.map((c) => ({
                  label: c?.name || c?.label || `Customer ${c?.id}`,
                  value: c?.id ?? c?.value,
                }))
              : []
          );
        }
      } catch (err) {
        console.error("Failed to fetch customer dropdown", err);
      }
    };
    fetchCustomers();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchInvoices = async () => {
      setLoading(true);

      try {
        const invoicesResponse = await apiService.getTripsInvoicesPaginated({
          page: currentPage,
          limit,
          search_text: searchText,
          searchFilters,
        });

        const invoicePageData =
          invoicesResponse?.data || invoicesResponse || {};
        const pageResults = safeArray(invoicePageData?.results).map((invoice) =>
          buildApiInvoiceRecord(invoice),
        );
        const nextMeta = invoicePageData?.meta || {};

        if (!isMounted) {
          return;
        }

        setRecords(pageResults);
        setPaginationMeta({
          totalItems: toNumber(
            nextMeta?.totalItems,
            toNumber(invoicePageData?.total, pageResults.length),
          ),
          totalPages: Math.max(1, toNumber(nextMeta?.totalPages, 1)),
          currentPage: Math.max(
            1,
            toNumber(nextMeta?.currentPage, currentPage),
          ),
        });

        const rawStats = invoicePageData?.stats || invoicePageData?.overview || {};
        setOverview({
          totalInvoices: toNumber(rawStats.total_invoices),
          outstandingAmount: toNumber(rawStats.outstanding_amount),
          paidAmount: toNumber(rawStats.paid_amount),
          totalRevenue: toNumber(rawStats.total_revenue),
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setRecords([]);
        setPaginationMeta({
          totalItems: 0,
          totalPages: 1,
          currentPage: 1,
        });
        errorNotify(error || "Failed to fetch invoices");
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchInvoices();

    return () => {
      isMounted = false;
    };
  }, [currentPage, limit, searchText, searchFilters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [limit, searchFilters, searchText]);

  useEffect(() => {
    if (!showCreateDropdown) return undefined;

    const handleOutsideClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCreateDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [showCreateDropdown]);

  const rows = useMemo(() => buildInvoiceTableRows(records), [records]);

  const totalItems = Math.max(
    rows.length,
    toNumber(paginationMeta.totalItems, rows.length),
  );
  const totalPages = Math.max(1, toNumber(paginationMeta.totalPages, 1));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const statsData = useMemo(
    () => [
      {
        title: "Total Invoices",
        value: formatAmount(overview.totalInvoices),
        text: <span>Total invoices created</span>,
        icon: buildStatIcon(<FiFileText />),
      },
      {
        title: "Account Receivables",
        value: formatAmount(overview.outstandingAmount),
        text: <span>Total Account Receivables</span>,
        className: "completed",
        icon: buildStatIcon(<FiFilePlus />),
      },
      {
        title: "Payments Received",
        value: formatAmount(overview.paidAmount),
        text: <span>Total Payments Received</span>,
        className: "late",
        icon: buildStatIcon(<BsCheckCircleFill />),
      },
      {
        title: "Total Revenue (PKR)",
        value: formatAmount(overview.totalRevenue),
        text: <span>Total Revenue generated</span>,
        className: "pending-delivery",
        icon: buildStatIcon(<BsCashStack />),
      },
    ],
    [overview],
  );

  const closeDeletePopup = () => {
    setDeletePopup(false);
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await apiService.deleteInvoice(deleteTarget.id);
      const nextRecords = records.filter(
        (record) => record.id !== deleteTarget.id,
      );
      setRecords(nextRecords);
      setPaginationMeta((previous) => {
        const nextTotalItems = Math.max(0, toNumber(previous.totalItems) - 1);
        return {
          ...previous,
          totalItems: nextTotalItems,
          totalPages: Math.max(1, Math.ceil(Math.max(1, nextTotalItems) / limit)),
        };
      });
      closeDeletePopup();
      successNotify("Invoice deleted successfully");
    } catch (error) {
      errorNotify(error || "Failed to delete invoice");
    }
  };

  const handleCreateOptionClick = (value) => {
    setShowCreateDropdown(false);

    if (value === "single-trip") {
      navigate("/invoices/create-single-trip");
      return;
    }

    if (value === "bulk") {
      navigate("/invoices/create-bulk");
    }
  };

  const getInvoiceRoute = (row = {}) => `/invoices/details/${row.id}`;

  const handleDownloadInvoice = async (row) => {
    if (!row?.id || downloadingInvoiceId) return;

    setDownloadingInvoiceId(String(row.id));

    try {
      const response = await apiService.exportTripInvoicePdf(row.id);
      const filePath =
        response?.data?.file_path ||
        response?.data?.filePath ||
        response?.data?.url ||
        response?.data ||
        response?.file_path ||
        response?.filePath ||
        response?.url ||
        "";
      const fileUrl = resolveApiFileUrl(filePath);

      if (!fileUrl) {
        errorNotify("Invoice PDF not found");
        return;
      }

      const anchor = document.createElement("a");
      anchor.href = fileUrl;
      anchor.target = "_blank";
      anchor.download = `${row.invoiceId || "invoice"}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      successNotify("Invoice downloaded successfully");
    } catch (error) {
      errorNotify(error || "Failed to download invoice PDF");
    } finally {
      setDownloadingInvoiceId("");
    }
  };

  const columns = [
    {
      name: "Invoice ID",
      selector: (row) => (
        <button
          type="button"
          className="finance_link_button finance_link_text"
          onClick={() => navigate(getInvoiceRoute(row))}
        >
          {row.invoiceId}
        </button>
      ),
      tableColumn: false,
      filterName: "invoiceIdFilter",
      type: "text",
      minWidth: "180px",
    },
    {
      name: "Customer",
      selector: (row) => row.customerName,
      tableColumn: true,
      filterName: "customerIdFilter",
      filterComponent: (props) => (
        <CustomerFilterDropdown
          {...props}
          options={customerOptions}
          columnName="Customer"
        />
      ),
      type: "select",
      minWidth: "200px",
    },
    {
      name: "Trips Count",
      selector: (row) => row.tripsCount,
      tableColumn: false,
      filterName: "tripsCountFilter",
      type: "text",
      minWidth: "160px",
    },
    {
      name: "Invoice Date",
      selector: (row) => row.invoiceDateDisplay,
      tableColumn: true,
      filterName: "invoiceDateFilter",
      filterComponent: DateFilterDropdown,
      type: "date_range",
      minWidth: "170px",
    },
    {
      name: "Total Amount",
      selector: (row) => `Rs. ${formatAmount(row.totalAmount)}`,
      tableColumn: false,
      filterName: "totalAmountFilter",
      type: "text",
      minWidth: "180px",
    },
    {
      name: "Status",
      selector: (row) => {
        const rawStatus = row.status || "";
        const displayLabel = rawStatus
          .split(/[-_]/)
          .map(
            (word) =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
          )
          .join(" ");

        let className = "is_submitted";
        const normalized = rawStatus.toLowerCase();
        if (normalized.includes("paid")) {
          className = "is_paid";
        } else if (
          normalized.includes("reject") ||
          normalized.includes("cancel")
        ) {
          className = "is_rejected";
        } else if (normalized === "new") {
          className = "is_new";
        }

        return (
          <span className={`invoice_status ${className}`}>
            <span>{displayLabel}</span>
          </span>
        );
      },
      tableColumn: true,
      filterName: "statusFilter",
      filterComponent: (props) => (
        <VehicleFilterDropdown
          {...props}
          options={invoiceStatusOptions}
          columnName="Status"
        />
      ),
      type: "select",
      options: invoiceStatusOptions,
      minWidth: "150px",
    },
    {
      name: "Created by",
      selector: (row) => (
        <div className="invoice_created_by_cell">
          <UserAvatar
            username={row.createdBy}
            image={row.createdByImage}
            marginRight="0px"
          />
          <span>{row.createdBy}</span>
        </div>
      ),
      tableColumn: false,
      filterName: "createdByFilter",
      type: "text",
      minWidth: "210px",
    },
    {
      name: "Action",
      selector: (row) => (
        <div className="table_action_btn">
          {canViewInvoices && (
            <button
              type="button"
              className="view_btn"
              title="View invoice"
              onClick={() => navigate(getInvoiceRoute(row))}
            >
              <LuEye />
            </button>
          )}
          {canViewInvoices && (
            <button
              type="button"
              className="download_btn"
              title="Download invoice"
              disabled={downloadingInvoiceId === String(row.id)}
              onClick={() => handleDownloadInvoice(row)}
            >
              <LuDownload />
            </button>
          )}
          {canDeleteInvoices && (row.status || "").toLowerCase() === "pending" && (
            <button
              type="button"
              className="delete_btn"
              title="Delete invoice"
              onClick={() => {
                setDeleteTarget(findInvoiceRecord(records, row.id));
                setDeletePopup(true);
              }}
            >
              <RiDeleteBinLine />
            </button>
          )}
        </div>
      ),
      tableColumn: false,
      minWidth: "135px",
      grow: 0,
      ignoreRowClick: true,
    },
  ];

  return (
    <div className="trip_cost_estimation_page invoices_page">
      <Breadcrumbs values={["Finance", "Invoices"]} />

      <div className="access_view">
        <h2>Invoices</h2>

        <div className="services_buttons">
          <ExportButton
            type="pdf"
            onClick={handleExportPdf}
            disabled={!hasActiveFilters || exportLoading}
            loading={pdfExportLoading}
            hasActiveFilters={hasActiveFilters}
          />

          <ExportButton
            type="excel"
            onClick={handleExportExcel}
            disabled={!hasActiveFilters || exportLoading}
            loading={excelExportLoading}
            hasActiveFilters={hasActiveFilters}
          />

          {canCreateInvoices && (
            <button type="button" onClick={() => navigate("/invoices/create")}>
              {plusIcon} Create Invoice
            </button>
          )}
        </div>
      </div>

      <Stats statsData={statsData} />

      <NewTableComponent
        columns={columns}
        data={rows}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        limit={limit}
        setLimit={setLimit}
        totalItems={totalItems}
        totalPages={totalPages}
        onSearch={setSearchText}
        searchValue={searchText}
        loading={loading}
        paginationLoading={loading}
        setSearchFilters={setSearchFilters}
        searchFilters={searchFilters}
        limitOptions={[10, 20, 50, 100]}
        tableClassName="invoices_table"
        noDataComponent={
          <div className="add_trip_suggestion_div driver_payments_empty_state">
            <h4>There is nothing here...</h4>
            <p>Create your first invoice to get started.</p>
            {canCreateInvoices && (
              <Button
                submitText="Create Invoice"
                type="button"
                shouldRow={false}
                isShowCancel={false}
                submitBtn={() => navigate("/invoices/create")}
              />
            )}
          </div>
        }
      />

      <ConfirmDeleteModal
        show={deletePopup}
        onClose={closeDeletePopup}
        onConfirm={handleDelete}
        title="Delete invoice?"
        description={`Deleting "${
          deleteTarget?.invoiceId || "this invoice"
        }" will permanently remove it from the list.`}
      />
    </div>
  );
};

export default Invoices;
